"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { InlineMath, BlockMath } from "react-katex"
import "katex/dist/katex.min.css"
import {
  Loader2,
  Plus,
  Pencil,
  Trash,
  Share2,Copy,
  Upload,
  ImageIcon,
  CheckSquare,
  FileText,
  AlignLeft,
  List,
  ChevronDown,
  ChevronUp,
  Download,
  Award,
  Save,
  AlertTriangle,
  CheckCircle,
  X,
  ArrowLeft,
  Info,
} from "lucide-react"
import { getQuizData } from "./api"
import { postTeacherQuizQuestionImage, generatePresignedUrl } from "../postFileAws"
import jsPDF from "jspdf"
import { postAws } from "../postAws"

export default function EditCreatedQuiz() {
  const { quizId } = useParams()
  const navigate = useNavigate()
  const [quiz, setQuiz] = useState({ quizQuestions: [] })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState(-1)
  const [editedQuestion, setEditedQuestion] = useState({})
  const [newQuestion, setNewQuestion] = useState({
    questionDifficulty: "Easy",
    questionType: "Multiple-Choice",
    question: "",
    questionTopic: "",
    options: ["", "", "", ""],
    correctAnswer: "",
    maxMarks: 1,
  })
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, index: -1 })
  const [uploadedImage, setUploadedImage] = useState(null)
  const [isImageUploading, setIsImageUploading] = useState(false)
  const [imageUrls, setImageUrls] = useState({})
  const [imageLoading, setImageLoading] = useState({})
  const [expandedQuestions, setExpandedQuestions] = useState({})

  const [isShareToClassModalOpen, setIsShareToClassModalOpen] = useState(false)
  const [selectedClass, setSelectedClass] = useState(null)
  const [availableClasses, setAvailableClasses] = useState([])
  const [isSharing, setIsSharing] = useState(false)
  const [shareSuccess, setShareSuccess] = useState(null)
  const organisationId = localStorage.getItem("organisationId")

  const teacherId = localStorage.getItem("teacherId")
  const classRoomId = localStorage.getItem("classRoomId")

  const handleBackToQuizzes = () => {
    navigate("/quiz")
  }

  useEffect(() => {
    const fetchQuiz = async () => {
      setIsLoading(true)
      try {
        const response = await getQuizData(quizId)
        if (response && response.statusCode === 200 && response.body) {
          const parsedBody = JSON.parse(response.body)
          if (parsedBody.data) {
            const quizData = parsedBody.data

            if (!quizData.quizTitle || quizData.quizTitle.trim() === "") {
              quizData.quizTitle = quizData.quiz_topic || "Untitled Quiz"
            }

            const quizQuestions = Array.isArray(quizData.quizQuestions) ? quizData.quizQuestions : []

            setQuiz({
              ...quizData,
              quizQuestions: quizQuestions,
            })
            localStorage.setItem("quizData", JSON.stringify(quizData))

            // Initialize questions as collapsed on mobile, expanded on desktop
            const expanded = {}
            const isMobileView = window.innerWidth < 768
            quizQuestions.forEach((q, index) => {
              expanded[index] = !isMobileView // false for mobile, true for desktop
            })
            setExpandedQuestions(expanded)

            const urls = {}
            const loadingState = {}
            for (const question of quizQuestions) {
              if (question.questionImageUrl) {
                loadingState[question.id] = true
                setImageLoading((prev) => ({ ...prev, [question.id]: true }))
                try {
                  const url = await generatePresignedUrl(question.questionImageUrl)
                  urls[question.id] = url
                  loadingState[question.id] = false
                } catch (error) {
                  console.error("Error generating presigned URL:", error)
                  loadingState[question.id] = false
                }
              }
            }
            setImageUrls(urls)
            setImageLoading(loadingState)
          } else {
            throw new Error("Quiz data not found in the response")
          }
        } else {
          throw new Error("Invalid response format from API")
        }
      } catch (err) {
        console.error("Error fetching quiz:", err)
        setError("Failed to load quiz. Please try again.")
      } finally {
        setIsLoading(false)
      }
    }

    if (quizId) {
      fetchQuiz()
    }

    fetchAvailableClasses()

    // Add resize listener to handle orientation changes
    const handleResize = () => {
      if (quiz && quiz.quizQuestions) {
        const isMobileView = window.innerWidth < 768
        const expanded = {}
        quiz.quizQuestions.forEach((_, index) => {
          expanded[index] = !isMobileView // false for mobile, true for desktop
        })
        setExpandedQuestions(expanded)
      }
    }

    window.addEventListener("resize", handleResize)

    return () => {
      localStorage.removeItem("quizData")
      window.removeEventListener("resize", handleResize)
    }
  }, [quizId])

  const fetchAvailableClasses = () => {
    const teacherData = JSON.parse(localStorage.getItem("teacherData"))
    if (teacherData && teacherData.createdClasses) {
      const currentClassRoomId = localStorage.getItem("classRoomId")
      const otherClasses = teacherData.createdClasses.filter((cls) => cls.classRoomId !== currentClassRoomId)
      setAvailableClasses(otherClasses)
    }
  }

  const toggleQuestionExpand = (index) => {
    setExpandedQuestions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  const getQuestionTypeIcon = (questionType) => {
    switch (questionType) {
      case "Multiple-Choice":
      case "MCQ":
        return <CheckSquare className="w-5 h-5 text-indigo-500" />
      case "Short Answer":
      case "Short-Answer":
        return <FileText className="w-5 h-5 text-emerald-500" />
      case "Essay":
        return <AlignLeft className="w-5 h-5 text-purple-500" />
      default:
        return <List className="w-5 h-5 text-gray-500" />
    }
  }

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "easy":
        return "text-emerald-500 bg-emerald-50"
      case "medium":
        return "text-amber-500 bg-amber-50"
      case "hard":
        return "text-rose-500 bg-rose-50"
      default:
        return "text-gray-500 bg-gray-50"
    }
  }

  const renderMathText = (text) => {
    if (!text) return null
    
    // Handle both block math ($$...$$) and inline math ($...$)
    const parts = text.split(/(\$\$[\s\S]*?\$\$|\$[^$\n]*?\$)/g)
    
    return parts.map((part, i) => {
      if (part.startsWith("$$") && part.endsWith("$$")) {
        const mathContent = part.slice(2, -2).trim()
        return mathContent ? <BlockMath key={i} math={mathContent} /> : null
      } else if (part.startsWith("$") && part.endsWith("$") && part.length > 2) {
        const mathContent = part.slice(1, -1).trim()
        return mathContent ? <InlineMath key={i} math={mathContent} /> : null
      } else {
        // Handle basic markdown formatting
        return (
          <span key={i} dangerouslySetInnerHTML={{
            __html: part
              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              .replace(/\*(.*?)\*/g, '<em>$1</em>')
              .replace(/`(.*?)`/g, '<code>$1</code>')
              .replace(/\n/g, '<br>')
          }} />
        )
      }
    }).filter(Boolean)
  }

  const handleShareQuiz = async () => {
    if (quiz.shareWithStudents) {
      alert("This quiz has already been shared with students and cannot be shared again.")
      return
    }
    setIsShareModalOpen(false)
    setIsLoading(true)

    const quizData = {
      ...quiz,
      shareWithStudents: true,
      quizId: quizId,
    }

    try {
      const body = quizData
      const quizToBeUpdated = {
        request: "updateQuiz",
        body,
      }
      const url = `https://api-dev.aurait.ai/quiz/Update?id=${quizId}`
      const response = await postAws(url, "POST", quizToBeUpdated)

      if (response?.statusCode === 200) {
        localStorage.setItem("quizData", JSON.stringify(quizData))
        setTimeout(() => {
          setIsLoading(false)
          navigate("/quiz")
        }, 2000)
      } else {
        throw new Error(response?.message || "Failed to update quiz")
      }
    } catch (error) {
      console.error("Error updating quiz:", error)
      setIsLoading(false)
      setError("Failed to share quiz. Please try again.")
    }
  }

  const handleSaveWithoutSharing = async () => {
    if (quiz.shareWithStudents) {
      alert("This quiz has already been shared with students and cannot be edited.")
      return
    }
    setIsLoading(true)

    const quizData = {
      ...quiz,
      shareWithStudents: quiz.shareWithStudents,
      quizId: quizId,
    }

    try {
      const body = quizData
      const quizToBeUpdated = {
        request: "updateQuiz",
        body,
      }
      const url = `https://api-dev.aurait.ai/quiz/Update?id=${quizId}`
      const response = await postAws(url, "POST", quizToBeUpdated)

      if (response?.statusCode === 200) {
        localStorage.setItem("quizData", JSON.stringify(quizData))
        setTimeout(() => {
          setIsLoading(false)
          navigate("/quiz")
        }, 2000)
      } else {
        throw new Error(response?.message || "Failed to update quiz")
      }
    } catch (error) {
      console.error("Error updating quiz:", error)
      setIsLoading(false)
      setError("Failed to save quiz. Please try again.")
    }
  }

  const handleEdit = (index) => {
    if (quiz.shareWithStudents) {
      alert("This quiz has already been shared with students and cannot be edited.")
      return
    }
    const questionToEdit = quiz.quizQuestions[index]
    setEditingIndex(index)
    setEditedQuestion({
      ...questionToEdit,
      questionType: "Multiple-Choice",
      mcqOptions: questionToEdit.options || [],
    })
    setUploadedImage(questionToEdit.questionImageUrl ? imageUrls[questionToEdit.id] : null)
    setIsEditModalOpen(true)
  }

  const handleSaveEdit = () => {
    if (quiz.shareWithStudents) {
      alert("This quiz has already been shared with students and cannot be edited.")
      return
    }
    if (editingIndex !== -1) {
      if (!editedQuestion.question.trim()) {
        alert("Error: Question is compulsory. Please enter a question.")
        return
      }
      if (editedQuestion.mcqOptions.filter((option) => option.trim()).length < 2) {
        alert("Error: For MCQ, please add at least 2 options.")
        return
      }
      if (!editedQuestion.correctAnswer) {
        alert("Error: Please select a correct answer.")
        return
      }
      setQuiz((prev) => {
        const updatedQuiz = {
          ...prev,
          quizQuestions: prev.quizQuestions.map((q, i) =>
            i === editingIndex
              ? {
                  ...editedQuestion,
                  maxMarks: editedQuestion.maxMarks || 0,
                  questionImageUrl: uploadedImage || editedQuestion.questionImageUrl,
                  questionImageUri: editedQuestion.questionImageUri,
                  options: editedQuestion.mcqOptions,
                }
              : q,
          ),
        }
        localStorage.setItem("quizData", JSON.stringify(updatedQuiz))
        return updatedQuiz
      })
      if (uploadedImage) {
        setImageUrls((prev) => ({
          ...prev,
          [editedQuestion.id]: uploadedImage,
        }))
      }
      setIsEditModalOpen(false)
      setEditingIndex(-1)
      setEditedQuestion({})
      setUploadedImage(null)
    }
  }

  const handleDelete = (index) => {
    if (quiz.shareWithStudents) {
      alert("This quiz has already been shared with students and cannot be edited.")
      return
    }
    setDeleteConfirmation({ isOpen: true, index })
  }

  const confirmDelete = () => {
    if (quiz.shareWithStudents) {
      alert("This quiz has already been shared with students and cannot be edited.")
      return
    }
    setQuiz((prev) => {
      const updatedQuiz = {
        ...prev,
        quizQuestions: prev.quizQuestions.filter((_, i) => i !== deleteConfirmation.index),
      }
      localStorage.setItem("quizData", JSON.stringify(updatedQuiz))
      return updatedQuiz
    })
    setDeleteConfirmation({ isOpen: false, index: -1 })
  }

  const handleImageUpload = async (event, isNewQuestion = false) => {
    if (quiz.shareWithStudents) {
      alert("This quiz has already been shared with students and cannot be edited.")
      return
    }
    const file = event.target.files[0]
    if (file) {
      setIsImageUploading(true)
      try {
        const quizName = quiz.quiz_topic
        const questionNumber = isNewQuestion ? `q${quiz.quizQuestions.length + 1}` : `q${editingIndex + 1}`
        const filename = file.name

        const result = await postTeacherQuizQuestionImage(
          file,
          teacherId,
          quizName,
          filename,
          questionNumber,
          classRoomId,
        )

        const presignedUrl = await generatePresignedUrl(result.uri)

        setUploadedImage(presignedUrl)
        if (isNewQuestion) {
          setNewQuestion((prev) => ({
            ...prev,
            questionImageUrl: result.url,
            questionImageUri: result.uri,
          }))
        } else {
          setEditedQuestion((prev) => ({
            ...prev,
            questionImageUrl: result.url,
            questionImageUri: result.uri,
          }))
        }
      } catch (error) {
        console.error("Error uploading image:", error)
        alert("Failed to upload image. Please try again.")
      } finally {
        setIsImageUploading(false)
      }
    }
  }

  const handleAddQuestion = () => {
    if (quiz.shareWithStudents) {
      alert("This quiz has already been shared with students and cannot be edited.")
      return
    }
    if (!newQuestion.question.trim()) {
      alert("Error: Question is compulsory. Please enter a question.")
      return
    }
    if (newQuestion.options.filter((option) => option.trim()).length < 2) {
      alert("Error: For MCQ, please add at least 2 options.")
      return
    }
    if (!newQuestion.correctAnswer) {
      alert("Error: Please select a correct answer.")
      return
    }
    const newQuestionId = `new-question-${Date.now()}`
    const newQuestionData = {
      ...newQuestion,
      id: newQuestionId,
    }

    if (uploadedImage) {
      setImageUrls((prev) => ({
        ...prev,
        [newQuestionId]: uploadedImage,
      }))
    }

    setQuiz((prev) => {
      const updatedQuiz = {
        ...prev,
        quizQuestions: [...prev.quizQuestions, newQuestionData],
      }
      localStorage.setItem("quizData", JSON.stringify(updatedQuiz))
      return updatedQuiz
    })

    // Set expansion state based on device type
    const isMobileView = window.innerWidth < 768
    setExpandedQuestions((prev) => ({
      ...prev,
      [quiz.quizQuestions.length]: !isMobileView, // collapsed on mobile, expanded on desktop
    }))

    setIsAddModalOpen(false)
    setNewQuestion({
      questionDifficulty: "Easy",
      questionType: "Multiple-Choice",
      question: "",
      questionTopic: "",
      options: ["", "", "", ""],
      correctAnswer: "",
      maxMarks: 1,
    })
    setUploadedImage(null)
  }

  const downloadAsPDF = () => {
    const doc = new jsPDF()
    let yOffset = 20
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 20
    const contentWidth = pageWidth - 2 * margin

    const addWrappedText = (text, x, y, maxWidth, lineHeight) => {
      const lines = doc.splitTextToSize(text, maxWidth)
      doc.text(lines, x, y)
      return lines.length * lineHeight
    }

    // Add title
    doc.setFontSize(18)
    doc.setFont("helvetica", "bold")
    yOffset += addWrappedText(quiz.quizTitle, margin, yOffset, contentWidth, 10)

    // Add quiz details
    doc.setFontSize(12)
    doc.setFont("helvetica", "normal")
    yOffset += 10
    yOffset += addWrappedText(`Topic: ${quiz.quiz_topic}`, margin, yOffset, contentWidth, 7)
    yOffset += 5
    yOffset += addWrappedText(
      `Created by: ${JSON.parse(localStorage.getItem("teacherData"))?.teacherName || "Teacher"}`,
      margin,
      yOffset,
      contentWidth,
      7,
    )
    yOffset += 10

    // Add questions
    quiz.quizQuestions.forEach((question, index) => {
      if (yOffset > 250) {
        doc.addPage()
        yOffset = 20
      }

      doc.setFontSize(14)
      doc.setFont("helvetica", "bold")
      yOffset += addWrappedText(`Question ${index + 1}:`, margin, yOffset, contentWidth, 7)
      yOffset += 5

      doc.setFontSize(12)
      doc.setFont("helvetica", "normal")
      yOffset += addWrappedText(question.question, margin, yOffset, contentWidth, 7)
      yOffset += 5

      yOffset += addWrappedText(
        `Type: Multiple-Choice | Difficulty: ${question.questionDifficulty} | Max Marks: ${question.maxMarks}`,
        margin,
        yOffset,
        contentWidth,
        7,
      )
      yOffset += 10

      question.options.forEach((option, optionIndex) => {
        yOffset += addWrappedText(
          `${String.fromCharCode(97 + optionIndex)}. ${option}`,
          margin + 10,
          yOffset,
          contentWidth - 10,
          7,
        )
        yOffset += 5
      })

      if (question.questionImageUrl) {
        try {
          doc.addImage(imageUrls[question.id] || question.questionImageUrl, "JPEG", margin, yOffset, 100, 100)
          yOffset += 110
        } catch (error) {
          console.error("Error adding image to PDF:", error)
        }
      }

      yOffset += 10
    })

    doc.save(`${quiz.quizTitle}.pdf`)
  }

  const handleShareToClass = async () => {
    if (!selectedClass) return

    setIsSharing(true)
    setShareSuccess(null)

    const latestQuizData = JSON.parse(localStorage.getItem("quizData"))

    const quizDataToShare = {
      ...latestQuizData,
      classCode: selectedClass.classCode,
      organisationId: organisationId,
      classRoomId: selectedClass.classRoomId,
      shareWithStudents: false,
    }

    try {
      const response = await postAws("https://api-dev.aurait.ai/quiz/Update", "POST", {
        body: quizDataToShare,
        request: "createQuiz",
      })

      if (response.statusCode === 201) {
        setShareSuccess(`Quiz shared to ${selectedClass.classValue}-${selectedClass.subject} successfully!`)
        setTimeout(() => {
          setIsShareToClassModalOpen(false)
          setShareSuccess(null)
        }, 3000)
      } else {
        throw new Error(response.body ? JSON.parse(response.body).message : "Failed to share quiz")
      }
    } catch (error) {
      console.error("Error sharing quiz:", error)
      setShareSuccess("Failed to share quiz. Please try again.")
    } finally {
      setIsSharing(false)
    }
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-violet-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-xl flex flex-col items-center">
          <Loader2 className="animate-spin h-12 w-12 text-indigo-600 mb-4" />
          <p className="text-lg font-medium text-gray-700">Loading quiz data...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait while we fetch your quiz</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-violet-50 to-indigo-100 p-4">
        <div className="bg-white p-8 rounded-xl shadow-xl text-center max-w-md">
          <AlertTriangle className="mx-auto text-red-500 mb-4 h-12 w-12" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Quiz</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate("/quiz")}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Return to Quizzes
          </button>
        </div>
      </div>
    )
  }

  if (!quiz) {
    return null
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 py-6 -mt-5">
        {/* Header Section */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-3 md:px-4 py-[0.5px]">
            {/* Mobile Layout */}
            <div className="block lg:hidden">
              <div className="flex items-center justify-between">
                <button
                  onClick={handleBackToQuizzes}
                  className="flex items-center text-white hover:text-gray-200 transition-colors duration-200"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  <span className="font-medium text-xs">Back</span>
                </button>

                <div className="flex items-center gap-2">
                  {/* Shared Quiz Warning */}
                  {quiz.shareWithStudents && (
                    <div className="flex items-center bg-amber-500 text-white px-2 py-0.5 rounded-full text-[10px] font-medium">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Shared
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={downloadAsPDF}
                      className="flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-2 py-1.5 rounded-lg transition-colors shadow-sm text-xs"
                    >
                      <Download className="w-3 h-3" />
                    </button>

                    <button
                      onClick={() => setIsShareModalOpen(true)}
                      className={`flex items-center justify-center bg-green-600 hover:bg-green-700 text-white px-2 py-1.5 rounded-lg transition-colors shadow-sm text-xs ${
                        quiz.shareWithStudents ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      disabled={quiz.shareWithStudents}
                    >
                      <Share2 className="w-3 h-3" />
                    </button>

                    <button
                      onClick={() => setIsShareToClassModalOpen(true)}
                      className="flex items-center justify-center bg-purple-600 hover:bg-purple-700 text-white px-2 py-1.5 rounded-lg transition-colors shadow-sm text-xs"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Centered Title */}
              <div className="text-center">
                <h1 className="text-white text-base font-bold -mt-1">{quiz.quizTitle || "Untitled Quiz"}</h1>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:block">
              <div className="flex items-center justify-between">
                <button
                  onClick={handleBackToQuizzes}
                  className="flex items-center text-white hover:text-gray-200 transition-colors duration-200"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  <span className="font-medium text-sm">Quizzes</span>
                </button>

                <div className="flex items-center gap-3">
                  {/* Shared Quiz Warning */}
                  {quiz.shareWithStudents && (
                    <div className="flex items-center bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Shared - Cannot be edited
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={downloadAsPDF}
                      className="flex items-center justify-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-colors shadow-sm text-sm"
                    >
                      <Download className="w-4 h-4" />
                      <span>PDF</span>
                    </button>

                    <button
                      onClick={() => setIsShareModalOpen(true)}
                      className={`flex items-center justify-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg transition-colors shadow-sm text-sm ${
                        quiz.shareWithStudents ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      disabled={quiz.shareWithStudents}
                    >
                      <Share2 className="w-4 h-4" />
                      <span>Share</span>
                    </button>

                    <button
                      onClick={() => setIsShareToClassModalOpen(true)}
                      className="flex items-center justify-center gap-1 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg transition-colors shadow-sm text-sm"
                    >
                      <Copy className="w-4 h-4" />
                      <span>Copy</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Centered Title */}
              <div className="text-center mt-1">
                <h1 className="text-white text-xl lg:text-2xl font-bold">{quiz.quizTitle || "Untitled Quiz"}</h1>
              </div>
            </div>
          </div>
        </div>

        {/* Questions Section */}
        <div className="space-y-4 mb-6">
          {quiz.quizQuestions &&
            quiz.quizQuestions.map((question, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                {/* Question Header - Only show question number and basic info, not the full question text */}
                <div
                  className="flex justify-between items-center p-3 md:p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleQuestionExpand(index)}
                >
                  <div className="flex items-center gap-2 md:gap-3 overflow-hidden">
                    <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-full bg-indigo-100 text-indigo-700 font-semibold text-xs md:text-sm">
                      {index + 1}
                    </div>
                    <div className="overflow-hidden">
                      <div className="flex items-center gap-1 md:gap-2">
                        <span className="flex-shrink-0">{getQuestionTypeIcon("Multiple-Choice")}</span>
                        <h2 className="font-semibold text-gray-800 text-sm md:text-base">
                          Question {index + 1}
                        </h2>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 md:gap-3 ml-2 flex-shrink-0">
                    <span className="flex items-center text-xs">
                      <Award className="w-3 h-3 md:w-4 md:h-4 text-amber-500 mr-1" />
                      <span className="text-gray-600">{question.maxMarks || 0}</span>
                    </span>
                    <span
                      className={`inline-block px-1.5 py-0.5 md:px-2 md:py-1 rounded-full text-[10px] md:text-xs font-medium ${getDifficultyColor(question.questionDifficulty)}`}
                    >
                      {question.questionDifficulty || "N/A"}
                    </span>
                    {expandedQuestions[index] ? (
                      <ChevronUp className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Question Content - Only show when expanded */}
                {expandedQuestions[index] && (
                  <div className="px-3 py-3 md:p-4 md:pt-0 border-t border-gray-100">
                    <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap gap-1.5 md:gap-2 mb-2 md:mb-3">
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-indigo-100 text-indigo-700">
                            Multiple-Choice
                          </span>
                          {question.questionTopic && (
                            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-purple-100 text-purple-700">
                              Topic: {question.questionTopic}
                            </span>
                          )}
                        </div>

                        <div className="text-gray-700 whitespace-pre-wrap mb-3 md:mb-4 bg-gray-50 p-3 md:p-4 rounded-lg text-sm md:text-base">
                          {renderMathText(question.question)}
                        </div>

                        {/* MCQ Options */}
                        <div className="mb-3 md:mb-4">
                          <h3 className="text-xs md:text-sm font-medium text-gray-500 mb-1.5 md:mb-2">
                            Answer Options:
                          </h3>
                          <div className="space-y-1.5 md:space-y-2 pl-1 md:pl-2">
                            {question.options.map((option, i) => (
                              <div
                                key={i}
                                className={`p-2 md:p-3 rounded-lg border ${
                                  option === question.correctAnswer ? "border-green-200 bg-green-50" : "border-gray-200"
                                }`}
                              >
                                <div className="flex items-start">
                                  <span className="flex-shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] md:text-xs font-medium text-gray-700 mr-2 md:mr-3">
                                    {String.fromCharCode(97 + i).toUpperCase()}
                                  </span>
                                  <div className="flex-1 text-sm md:text-base">
                                    <span className={`${option === question.correctAnswer ? "font-medium" : ""}`}>
                                      {renderMathText(option)}
                                    </span>
                                    {option === question.correctAnswer && (
                                      <span className="ml-1 md:ml-2 text-green-600 text-xs md:text-sm font-medium flex items-center">
                                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                                        Correct
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Question Image */}
                        {question.questionImageUrl && (
                          <div className="mt-4">
                            {imageLoading[question.id] ? (
                              <div className="flex items-center justify-center h-40 bg-gray-100 rounded-lg">
                                <Loader2 className="animate-spin h-8 w-8 text-indigo-500" />
                                <span className="ml-2 text-sm text-gray-600">Loading image...</span>
                              </div>
                            ) : (
                              <div className="bg-gray-100 p-2 rounded-lg">
                                <img
                                  src={imageUrls[question.id] || question.questionImageUrl}
                                  alt="Question"
                                  className="max-w-full h-auto rounded-lg mx-auto"
                                  onError={(e) => {
                                    e.target.onerror = null
                                    e.target.src = "/placeholder.svg"
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Question Actions */}
                      <div className="flex md:flex-col gap-1.5 md:gap-2 justify-end">
                        <div className="flex items-center gap-1 md:gap-2 bg-gray-50 p-1.5 md:p-2 rounded-lg mb-1 md:mb-2">
                          <label
                            htmlFor={`marks-${index}`}
                            className="text-xs md:text-sm text-gray-600 whitespace-nowrap"
                          >
                            Marks:
                          </label>
                          <input
                            id={`marks-${index}`}
                            type="number"
                            value={question.maxMarks || 0}
                            onChange={(e) => {
                              if (quiz.shareWithStudents) return
                              const newMarks = Number.parseInt(e.target.value, 10)
                              setQuiz((prev) => {
                                const updatedQuiz = {
                                  ...prev,
                                  quizQuestions: prev.quizQuestions.map((q, i) =>
                                    i === index ? { ...q, maxMarks: newMarks } : q,
                                  ),
                                }
                                localStorage.setItem("quizData", JSON.stringify(updatedQuiz))
                                return updatedQuiz
                              })
                            }}
                            className="w-12 md:w-16 px-1.5 md:px-2 py-0.5 md:py-1 border rounded text-right text-sm"
                            min="0"
                            disabled={quiz.shareWithStudents}
                          />
                        </div>
                        <div className="flex md:flex-col gap-1.5 md:gap-2">
                          <button
                            onClick={() => handleEdit(index)}
                            className={`p-1.5 md:p-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors ${
                              quiz.shareWithStudents ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                            aria-label="Edit question"
                            disabled={quiz.shareWithStudents}
                          >
                            <Pencil className="w-4 h-4 md:w-5 md:h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(index)}
                            className={`p-1.5 md:p-2 bg-rose-100 text-rose-700 rounded-lg hover:bg-rose-200 transition-colors ${
                              quiz.shareWithStudents ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                            aria-label="Delete question"
                            disabled={quiz.shareWithStudents}
                          >
                            <Trash className="w-4 h-4 md:w-5 md:h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
        </div>

        {/* Footer Section */}
        <div className="bg-white rounded-xl shadow-md p-3 md:p-5 flex flex-col sm:flex-row justify-between items-center gap-3 md:gap-4">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="bg-indigo-100 text-indigo-700 p-2 md:p-3 rounded-full">
              <Award className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            <div>
              <p className="text-xs md:text-sm text-gray-500">Total Marks</p>
              <p className="text-xl md:text-2xl font-bold text-gray-800">
                {quiz.quizQuestions ? quiz.quizQuestions.reduce((total, q) => total + (q.maxMarks || 0), 0) : 0}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 md:gap-3 justify-center sm:justify-end">
            <button
              onClick={() => setIsAddModalOpen(true)}
              className={`px-3 md:px-5 py-2 md:py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1 md:gap-2 shadow-sm text-xs md:text-sm ${
                quiz.shareWithStudents ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={quiz.shareWithStudents}
            >
              <Plus className="w-4 h-4 md:w-5 md:h-5" />
              Add Question
            </button>
            <button
              onClick={handleSaveWithoutSharing}
              className={`px-3 md:px-5 py-2 md:py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-1 md:gap-2 shadow-sm text-xs md:text-sm ${
                quiz.shareWithStudents ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={quiz.shareWithStudents}
            >
              <Save className="w-4 h-4 md:w-5 md:h-5" />
              Save Draft
            </button>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Edit Question</h2>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-white hover:bg-white/20 rounded-full p-1.5 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Question Type</label>
                  <input
                    type="text"
                    value="Multiple-Choice"
                    disabled
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-gray-100"
                  />
                </div>
                <div>
                  <label htmlFor="questionDifficulty" className="block text-sm font-medium text-gray-700 mb-1">
                    Difficulty
                  </label>
                  <select
                    id="questionDifficulty"
                    value={editedQuestion.questionDifficulty || "Easy"}
                    onChange={(e) => setEditedQuestion({ ...editedQuestion, questionDifficulty: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="questionText" className="block text-sm font-medium text-gray-700 mb-1">
                  Question Text
                </label>
                <textarea
                  id="questionText"
                  value={editedQuestion.question || ""}
                  onChange={(e) => setEditedQuestion({ ...editedQuestion, question: e.target.value })}
                  className="w-full h-32 p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter question text"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="questionTopic" className="block text-sm font-medium text-gray-700 mb-1">
                  Topic
                </label>
                <input
                  type="text"
                  id="questionTopic"
                  value={editedQuestion.questionTopic || ""}
                  onChange={(e) => setEditedQuestion({ ...editedQuestion, questionTopic: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter question topic"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Answer Options</label>
                {(editedQuestion.mcqOptions || []).map((option, index) => (
                  <div key={index} className="flex mb-2">
                    <div className="flex-shrink-0 mr-2 flex items-center">
                      <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-700">
                        {String.fromCharCode(97 + index).toUpperCase()}
                      </span>
                    </div>
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...(editedQuestion.mcqOptions || [])]
                        newOptions[index] = e.target.value
                        setEditedQuestion({ ...editedQuestion, mcqOptions: newOptions })
                      }}
                      placeholder={`Option ${index + 1}`}
                      className="flex-grow p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <input
                      type="radio"
                      name="correctAnswer"
                      checked={editedQuestion.correctAnswer === option}
                      onChange={() => setEditedQuestion({ ...editedQuestion, correctAnswer: option })}
                      className="ml-2"
                    />
                    <label className="ml-1 text-sm">Correct</label>
                    {index > 1 && (
                      <button
                        onClick={() => {
                          const newOptions = (editedQuestion.mcqOptions || []).filter((_, i) => i !== index)
                          setEditedQuestion({ ...editedQuestion, mcqOptions: newOptions })
                        }}
                        className="ml-2 p-2 bg-rose-100 text-rose-700 rounded-lg hover:bg-rose-200 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                {(editedQuestion.mcqOptions || []).length < 4 && (
                  <button
                    onClick={() => {
                      const newOptions = [...(editedQuestion.mcqOptions || []), ""]
                      setEditedQuestion({ ...editedQuestion, mcqOptions: newOptions })
                    }}
                    className="w-full py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Option
                  </button>
                )}
              </div>

              <div className="mb-4">
                <label htmlFor="maxMarks" className="block text-sm font-medium text-gray-700 mb-1">
                  Max Marks
                </label>
                <input
                  type="number"
                  id="maxMarks"
                  value={editedQuestion.maxMarks || 0}
                  onChange={(e) => setEditedQuestion({ ...editedQuestion, maxMarks: Number(e.target.value) })}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  min="0"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="imageUpload" className="block text-sm font-medium text-gray-700 mb-2">
                  Question Image
                </label>
                <div className="mt-1 flex flex-col sm:flex-row items-center gap-4">
                  <div className="w-full sm:w-32 h-32 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                    {(uploadedImage || editedQuestion.questionImageUrl) && !isImageUploading ? (
                      <img
                        src={uploadedImage || imageUrls[editedQuestion.id] || editedQuestion.questionImageUrl}
                        alt="Question"
                        className="h-full w-full object-contain"
                        onError={(e) => {
                          e.target.onerror = null
                          e.target.src = "/placeholder.svg"
                        }}
                      />
                    ) : (
                      <ImageIcon className="h-12 w-12 text-gray-300" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <div className="flex flex-wrap gap-2">
                      <input
                        type="file"
                        id="imageUpload"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <label
                        htmlFor="imageUpload"
                        className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors cursor-pointer flex items-center gap-2"
                      >
                        <Upload className="w-4 h-4" />
                        {uploadedImage || editedQuestion.questionImageUrl ? "Change Image" : "Upload Image"}
                      </label>
                      {(uploadedImage || editedQuestion.questionImageUrl) && (
                        <button
                          type="button"
                          onClick={() => {
                            setUploadedImage(null)
                            setEditedQuestion((prev) => ({ ...prev, questionImageUrl: null, questionImageUri: null }))
                          }}
                          className="px-4 py-2 bg-rose-100 text-rose-700 rounded-lg hover:bg-rose-200 transition-colors flex items-center gap-2"
                        >
                          <Trash className="w-4 h-4" />
                          Remove
                        </button>
                      )}
                    </div>
                    {isImageUploading && (
                      <div className="flex items-center text-sm text-gray-500">
                        <Loader2 className="animate-spin mr-2" size={16} />
                        <span>Uploading image...</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Question Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-green-600 to-teal-600 p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Add New Question</h2>
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="text-white hover:bg-white/20 rounded-full p-1.5 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Question Type</label>
                  <input
                    type="text"
                    value="Multiple-Choice"
                    disabled
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-gray-100"
                  />
                </div>
                <div>
                  <label htmlFor="newQuestionDifficulty" className="block text-sm font-medium text-gray-700 mb-1">
                    Difficulty
                  </label>
                  <select
                    id="newQuestionDifficulty"
                    value={newQuestion.questionDifficulty}
                    onChange={(e) => setNewQuestion({ ...newQuestion, questionDifficulty: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="newQuestionText" className="block text-sm font-medium text-gray-700 mb-1">
                  Question Text
                </label>
                <textarea
                  id="newQuestionText"
                  value={newQuestion.question}
                  onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                  className="w-full h-32 p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter question text"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="newQuestionTopic" className="block text-sm font-medium text-gray-700 mb-1">
                  Topic
                </label>
                <input
                  type="text"
                  id="newQuestionTopic"
                  value={newQuestion.questionTopic}
                  onChange={(e) => setNewQuestion({ ...newQuestion, questionTopic: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter question topic"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Answer Options</label>
                {newQuestion.options.map((option, index) => (
                  <div key={index} className="flex mb-2">
                    <div className="flex-shrink-0 mr-2 flex items-center">
                      <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-700">
                        {String.fromCharCode(97 + index).toUpperCase()}
                      </span>
                    </div>
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...newQuestion.options]
                        newOptions[index] = e.target.value
                        setNewQuestion({ ...newQuestion, options: newOptions })
                      }}
                      placeholder={`Option ${index + 1}`}
                      className="flex-grow p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <input
                      type="radio"
                      name="correctAnswer"
                      checked={newQuestion.correctAnswer === option}
                      onChange={() => setNewQuestion({ ...newQuestion, correctAnswer: option })}
                      className="ml-2"
                    />
                    <label className="ml-1 text-sm">Correct</label>
                    {index > 1 && (
                      <button
                        onClick={() => {
                          const newOptions = newQuestion.options.filter((_, i) => i !== index)
                          setNewQuestion({ ...newQuestion, options: newOptions })
                        }}
                        className="ml-2 p-2 bg-rose-100 text-rose-700 rounded-lg hover:bg-rose-200 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                {newQuestion.options.length < 4 && (
                  <button
                    onClick={() => {
                      const newOptions = [...newQuestion.options, ""]
                      setNewQuestion({ ...newQuestion, options: newOptions })
                    }}
                    className="w-full py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Option
                  </button>
                )}
              </div>

              <div className="mb-4">
                <label htmlFor="newMaxMarks" className="block text-sm font-medium text-gray-700 mb-1">
                  Max Marks
                </label>
                <input
                  type="number"
                  id="newMaxMarks"
                  value={newQuestion.maxMarks}
                  onChange={(e) => setNewQuestion({ ...newQuestion, maxMarks: Number(e.target.value) })}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  min="0"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="newImageUpload" className="block text-sm font-medium text-gray-700 mb-2">
                  Question Image
                </label>
                <div className="mt-1 flex flex-col sm:flex-row items-center gap-4">
                  <div className="w-full sm:w-32 h-32 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                    {uploadedImage && !isImageUploading ? (
                      <img
                        src={uploadedImage || "/placeholder.svg"}
                        alt="Question"
                        className="h-full w-full object-contain"
                        onError={(e) => {
                          e.target.onerror = null
                          e.target.src = "/placeholder.svg"
                        }}
                      />
                    ) : (
                      <ImageIcon className="h-12 w-12 text-gray-300" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <div className="flex flex-wrap gap-2">
                      <input
                        type="file"
                        id="newImageUpload"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, true)}
                        className="hidden"
                      />
                      <label
                        htmlFor="newImageUpload"
                        className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors cursor-pointer flex items-center gap-2"
                      >
                        <Upload className="w-4 h-4" />
                        {uploadedImage ? "Change Image" : "Upload Image"}
                      </label>
                      {uploadedImage && (
                        <button
                          type="button"
                          onClick={() => {
                            setUploadedImage(null)
                            setNewQuestion((prev) => ({ ...prev, questionImageUrl: null, questionImageUri: null }))
                          }}
                          className="px-4 py-2 bg-rose-100 text-rose-700 rounded-lg hover:bg-rose-200 transition-colors flex items-center gap-2"
                        >
                          <Trash className="w-4 h-4" />
                          Remove
                        </button>
                      )}
                    </div>
                    {isImageUploading && (
                      <div className="flex items-center text-sm text-gray-500">
                        <Loader2 className="animate-spin mr-2" size={16} />
                        <span>Uploading image...</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddQuestion}
                  className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Add Question
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {isShareModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="bg-gradient-to-r from-green-600 to-teal-600 p-4">
              <h2 className="text-xl font-bold text-white">Share Quiz</h2>
            </div>
            <div className="p-6">
              <div className="flex items-start mb-4">
                <Info className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0" />
                <p className="text-gray-700">
                  Are you sure you want to share this quiz with students? Once shared, the quiz cannot be edited.
                </p>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setIsShareModalOpen(false)}
                  className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleShareQuiz}
                  className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Confirm Share
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share to Class Modal */}
      {isShareToClassModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4">
              <h2 className="text-xl font-bold text-white">Share to Other Class</h2>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Destination Class</label>
              <select
                className="w-full p-2.5 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={selectedClass ? selectedClass.classRoomId : ""}
                onChange={(e) => setSelectedClass(availableClasses.find((c) => c.classRoomId === e.target.value))}
              >
                <option value="">Select a class</option>
                {availableClasses.map((cls) => (
                  <option key={cls.classRoomId} value={cls.classRoomId} disabled={cls.classRoomId === classRoomId}>
                    {cls.classValue}-{cls.subject} {cls.classRoomId === classRoomId ? "(Current Class)" : ""}
                  </option>
                ))}
              </select>

              {shareSuccess && (
                <div className="mb-4 p-3 bg-green-50 border-l-4 border-green-500 text-green-700 rounded-r-lg flex items-start">
                  <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                  <p>{shareSuccess}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setIsShareToClassModalOpen(false)}
                  className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleShareToClass}
                  disabled={!selectedClass || isSharing}
                  className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSharing ? (
                    <>
                      <Loader2 className="animate-spin w-4 h-4" />
                      <span>Sharing...</span>
                    </>
                  ) : (
                    <>
                      <Share2 className="w-4 h-4" />
                      <span>Share</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="bg-gradient-to-r from-rose-600 to-red-600 p-4">
              <h2 className="text-xl font-bold text-white">Confirm Deletion</h2>
            </div>
            <div className="p-6">
              <div className="flex items-start mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-500 mr-3 flex-shrink-0" />
                <p className="text-gray-700">
                  Are you sure you want to delete this question? This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setDeleteConfirmation({ isOpen: false, index: -1 })}
                  className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-5 py-2.5 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
)}